/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DeviceModelForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Device Models module.
----------------------------------------------------------------*/


import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { DeviceModelAdvancedSection } from './components/DeviceModelAdvancedSection';
import { DeviceModelFilePreview } from './components/DeviceModelFilePreview';
import { Smartphone, Upload, FileCode, Search, Keyboard, Eye, X, Download, Sun, Clock, Copy, Plus } from 'lucide-react';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { Toggle } from '../../components/ui/Toggle';

import { useDeviceModelsData } from './hooks/useDeviceModelsData';
import * as api from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { MultiSelectModal } from '../../components/ui/MultiSelectModal';

// Extracted modules
import { VendorSelect } from './components/VendorSelect';
import { DeviceModelFormData } from './types';
import {
    extractDssTypesFromKeys,
    extractTimezoneOptions,
    extractTimeFormatOptions,
    extractDateFormatOptions,
    extractLanguageOptions
} from './utils/extractOptions';


export default function DeviceModelForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { vendors, models, loading, error: loadError } = useDeviceModelsData();
    const { showSuccess, showError } = useNotifications();

    // File states
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [templateFile, setTemplateFile] = useState<File | null>(null);
    const [firmwareFile, setFirmwareFile] = useState<File | null>(null);
    const [existingFiles, setExistingFiles] = useState({ image: '', template: '', firmware: '' });
    const [detectedVariables, setDetectedVariables] = useState<string[]>([]);
    const [availableDssOptions, setAvailableDssOptions] = useState<{ value: string, label: string }[]>([]);
    const [showDssModal, setShowDssModal] = useState(false);
    const [availableTimezoneOptions, setAvailableTimezoneOptions] = useState<{ value: string, label: string }[]>([]);
    const [availableTimeFormatOptions, setAvailableTimeFormatOptions] = useState<{ value: string, label: string }[]>([]);
    const [availableDateFormatOptions, setAvailableDateFormatOptions] = useState<{ value: string, label: string }[]>([]);
    const [availableLanguageOptions, setAvailableLanguageOptions] = useState<{ value: string, label: string }[]>([]);

    const [showTimezoneModal, setShowTimezoneModal] = useState(false);
    const [showTimeFormatModal, setShowTimeFormatModal] = useState(false);
    const [showDateFormatModal, setShowDateFormatModal] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [showSummerTimeModal, setShowSummerTimeModal] = useState(false);
    const [showDaylightModal, setShowDaylightModal] = useState(false);

    const [showFilePreview, setShowFilePreview] = useState(false);
    const [fileContent, setFileContent] = useState('');
    const [previewFileName, setPreviewFileName] = useState('');

    const [showQuickList, setShowQuickList] = useState(false);
    const [quickListSearch, setQuickListSearch] = useState('');

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; modelId: number | string | null }>({ isOpen: false, modelId: null });
    const [isSystemModel, setIsSystemModel] = useState(false);

    const quickListItems = React.useMemo(() => {
        if (!quickListSearch) return models;
        const q = quickListSearch.toLowerCase();
        return models.filter(m => m.modelName.toLowerCase().includes(q) || m.vendor?.name?.toLowerCase().includes(q));
    }, [models, quickListSearch]);

    const isEdit = !!id && id !== 'new';

    const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm<DeviceModelFormData>({
        defaultValues: {
            vendorId: '', modelName: '', maxAccounts: 1, maxDssKeys: 0,
            supportsExpansionUnits: false, maxExpansionUnits: 0, expModuleKeys: 0,
            expansionUnitModels: '[]', supportedDssTypes: '[]', supportedTemplateTypes: '["cfg"]',
            timezoneOptions: '[]', timeFormatOptions: '[]', dateFormatOptions: '[]',
            languageOptions: '[]', wallpaper: false, summerTimeOptions: '[]',
            daylightSavingsOptions: '[]', isSystem: false
        }
    });

    const watchedSummerTime = watch('summerTimeOptions');
    const watchedDaylightSavings = watch('daylightSavingsOptions');

    const hasOptions = (jsonStr: string) => {
        try {
            const parsed = JSON.parse(jsonStr || '[]');
            if (Array.isArray(parsed)) return parsed.length > 0;
            if (typeof parsed === 'object' && parsed !== null) return Object.keys(parsed).length > 0;
            return false;
        } catch { return false; }
    };

    const showSummerTime = !isSystemModel || hasOptions(watchedSummerTime);
    const showDaylightSavings = !isSystemModel || hasOptions(watchedDaylightSavings);

    useEffect(() => {
        if (isEdit) {
            api.getDeviceModel(id).then(model => {
                const extractedTypes = extractDssTypesFromKeys(model.keys);
                const availableTypeNames = extractedTypes.map(t => t.value);
                const supportedTypes = (model.supportedDssTypes && model.supportedDssTypes.length > 0)
                    ? model.supportedDssTypes : availableTypeNames;

                reset({
                    vendorId: model.vendorId.toString(), modelName: model.modelName,
                    maxAccounts: model.maxAccounts, maxDssKeys: model.maxDssKeys,
                    supportsExpansionUnits: model.supportsExpansionUnits, maxExpansionUnits: model.maxExpansionUnits,
                    expModuleKeys: model.expModuleKeys || 0, expansionUnitModels: JSON.stringify(model.expansionUnitModels || []),
                    supportedDssTypes: JSON.stringify(supportedTypes), supportedTemplateTypes: JSON.stringify(model.supportedTemplateTypes || []),
                    timezoneOptions: typeof model.timezone === 'string' ? model.timezone : JSON.stringify(model.timezone || []),
                    timeFormatOptions: typeof model.timeFormat === 'string' ? model.timeFormat : JSON.stringify(model.timeFormat || {}),
                    dateFormatOptions: typeof model.dateFormat === 'string' ? model.dateFormat : JSON.stringify(model.dateFormat || {}),
                    languageOptions: '[]',
                    wallpaper: model.wallpaper || false,
                    summerTimeOptions: (model.summerTime && model.summerTime !== '[]') ? (typeof model.summerTime === 'string' ? model.summerTime : JSON.stringify(model.summerTime)) : '[]',
                    daylightSavingsOptions: (model.daylightSavings && model.daylightSavings !== '[]') ? (typeof model.daylightSavings === 'string' ? model.daylightSavings : JSON.stringify(model.daylightSavings)) : '[]',
                    isSystem: model.isSystem ?? false
                });
                setExistingFiles({ image: model.imageFile || '', template: model.templateFile || '', firmware: model.firmware || '' });
                setDetectedVariables(model.variablesJson || []);
                setIsSystemModel(model.isSystem || false);
                setAvailableDssOptions(extractedTypes);
                setAvailableTimezoneOptions(extractTimezoneOptions(model.timezone));
                setAvailableTimeFormatOptions(extractTimeFormatOptions(model.timeFormat));
                setAvailableDateFormatOptions(extractDateFormatOptions(model.dateFormat));

                let languageOpts = extractLanguageOptions(model.language);
                if (languageOpts.length === 0) {
                    languageOpts = [
                        { value: 'English', label: 'English', selected: true }, { value: 'Spanish', label: 'Spanish', selected: true },
                        { value: 'French', label: 'French', selected: true }, { value: 'German', label: 'German', selected: true },
                        { value: 'Italian', label: 'Italian', selected: true }, { value: 'Portuguese', label: 'Portuguese', selected: true },
                        { value: 'Russian', label: 'Russian', selected: true }, { value: 'Turkish', label: 'Turkish', selected: true },
                        { value: 'Dutch', label: 'Dutch', selected: true }, { value: 'Chinese_S', label: 'Chinese Simplified', selected: true }
                    ];
                }
                setAvailableLanguageOptions(languageOpts);
                reset((fv) => ({ ...fv, languageOptions: JSON.stringify(languageOpts) }));
            }).catch(() => showError('Failed to load model'));
        } else {
            setAvailableDssOptions([]); setAvailableTimezoneOptions([]); setAvailableTimeFormatOptions([]);
            setAvailableDateFormatOptions([]); setAvailableLanguageOptions([]);
        }
    }, [id, isEdit, reset, showError]);

    const actionRef = useRef<string>('save');
    const [isSaving, setIsSaving] = useState(false);

    const handlePreviewFile = async () => {
        if (templateFile) {
            const reader = new FileReader();
            reader.onload = (e) => { setFileContent(e.target?.result as string); setPreviewFileName(templateFile.name); setShowFilePreview(true); };
            reader.readAsText(templateFile);
        } else if (existingFiles.template) {
            try {
                const url = `/uploads/${existingFiles.template.replace(/\\/g, '/')}`;
                const res = await fetch(url);
                if (!res.ok) throw new Error('Failed to load file');
                setFileContent(await res.text());
                setPreviewFileName(existingFiles.template.split(/[/\\]/).pop() || 'Template');
                setShowFilePreview(true);
            } catch { showError('Could not load file content'); }
        }
    };

    const handleDownload = () => {
        const blob = new Blob([fileContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = previewFileName;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    };

    const onSubmit = async (data: DeviceModelFormData) => {
        setIsSaving(true);
        try {
            const formData = new FormData();
            const fieldMappings: Record<string, string> = { 'summerTimeOptions': 'summerTime', 'daylightSavingsOptions': 'daylightSavings' };
            Object.keys(data).forEach(key => { const bk = fieldMappings[key] || key; formData.append(bk, (data as never)[key]); });
            if (imageFile) formData.append('imageFile', imageFile);
            if (templateFile) formData.append('templateFile', templateFile);
            if (firmwareFile) formData.append('firmwareFile', firmwareFile);

            let savedId = id;
            if (isEdit) {
                await api.updateDeviceModel(id, formData);
                showSuccess(t('common.savedSuccessfully', 'Saved successfully'));
            } else {
                const response = await api.createDeviceModel(formData);
                savedId = response.id;
                showSuccess(t('common.createdSuccessfully', 'Created successfully'));
            }

            const currentAction = actionRef.current;
            if (currentAction === 'save-and-close') navigate('/settings/provisioning/provisioning-device-models');
            else if (currentAction === 'save-and-new') { navigate('/settings/provisioning/provisioning-device-models/new'); window.location.reload(); }
            else if (currentAction === 'save' && !isEdit && savedId) navigate(`/settings/provisioning/provisioning-device-models/${savedId}`, { replace: true });
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to save model');
        } finally { setIsSaving(false); }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (f: File | null) => void) => {
        if (e.target.files && e.target.files[0]) setter(e.target.files[0]);
    };

    const handleSaveAction = async (selectedAction: string) => {
        actionRef.current = selectedAction;
        await handleSubmit(onSubmit)();
    };

    const handleDelete = async () => {
        if (!deleteModal.modelId) return;
        try {
            await api.deleteDeviceModel(deleteModal.modelId);
            showSuccess('Model deleted successfully');
            setDeleteModal({ isOpen: false, modelId: null });
            navigate('/settings/provisioning/provisioning-device-models');
        } catch (err) { showError(err instanceof Error ? err.message : 'Failed to delete model'); }
    };

    const handleDuplicate = async () => {
        if (!id) return;
        try {
            const newModel = await api.duplicateDeviceModel(id);
            showSuccess(t('common.modelDuplicated', 'Model duplicated successfully'));
            navigate(`/settings/provisioning/provisioning-device-models/${newModel.id}`);
        } catch (err) { showError(err instanceof Error ? err.message : t('common.duplicateError', 'Failed to duplicate model')); }
    };

    // Grid layout helpers
    const visibleFeaturesCount = 1 + (showSummerTime ? 1 : 0) + (showDaylightSavings ? 1 : 0);
    const advRowsNeeded = Math.ceil(visibleFeaturesCount / 2);
    const totalGridRows = 3 + advRowsNeeded + 1;
    const imageRowSpanClass = totalGridRows === 6 ? 'row-span-6' : 'row-span-5';

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Smartphone className="h-5 w-5 text-primary" />}
                title={isEdit ? 'Edit Device Model' : 'Create Device Model'}
                onBack={() => navigate('/settings/provisioning/provisioning-device-models')}
                backButtonText={t('common.list', 'List')}
                actions={[
                    ...(isEdit ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.duplicate', 'Duplicate'), onClick: handleDuplicate }] : []),
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: 'Quick Search', onClick: () => setShowQuickList(true) },
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('provisioningDeviceModels.add'), onClick: () => navigate('/settings/provisioning/provisioning-device-models/new') }
                ]}
            />

            <div className="mt-8">
                <form id="device-model-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* General Information */}
                    <FormBox title="General Information" icon={<Smartphone className="h-5 w-5" />}>
                        <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_180px] gap-x-6 gap-y-3 items-center">
                            <SimpleTooltip content={isEdit ? "Vendor cannot be changed when editing" : "Select the vendor for this model"}>
                                <Label htmlFor="vendorId" required className="text-left">Vendor</Label>
                            </SimpleTooltip>
                            <Controller control={control} name="vendorId" rules={{ required: 'Vendor is required' }}
                                render={({ field }) => (
                                    <VendorSelect value={field.value} onChange={field.onChange} vendors={vendors} error={errors.vendorId?.message} disabled={isEdit} />
                                )} />
                            <SimpleTooltip content="Official model name (e.g. T54W)">
                                <Label htmlFor="modelName" required className="text-left">Model Name</Label>
                            </SimpleTooltip>
                            <Input id="modelName" {...register('modelName', { required: 'Model name is required' })} placeholder="e.g. T54W" />
                            {/* Image column � spans dynamic rows */}
                            <div className={`${imageRowSpanClass} flex flex-col items-center justify-center p-3 bg-muted/20 rounded-lg border border-dashed border-border h-full min-h-[160px]`}>
                                {existingFiles.image && !imageFile ? (
                                    <div className="flex-1 flex items-center justify-center">
                                        <img src={`/uploads/${existingFiles.image.replace(/\\/g, '/')}`} alt="Device Preview" className="max-h-28 w-auto object-contain" />
                                    </div>
                                ) : imageFile ? (
                                    <div className="flex-1 flex items-center justify-center">
                                        <img src={URL.createObjectURL(imageFile)} alt="New Image" className="max-h-28 w-auto object-contain" />
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col items-center justify-center">
                                        <Upload className="h-8 w-8 text-muted-foreground mb-1" /><span className="text-xs text-muted-foreground">Image</span>
                                    </div>
                                )}
                                <label className="mt-2 cursor-pointer">
                                    <span className="text-xs text-primary hover:text-primary/80 px-2 py-1 border border-primary/30 rounded">{existingFiles.image || imageFile ? 'Change' : 'Upload'}</span>
                                    <input type="file" accept="image/*" onChange={e => handleFileChange(e, setImageFile)} className="hidden" />
                                </label>
                            </div>

                            {/* Row 2 */}
                            <SimpleTooltip content="Maximum number of SIP accounts/lines"><Label htmlFor="maxAccounts" className="text-left">Max Accounts</Label></SimpleTooltip>
                            <Input id="maxAccounts" type="number" {...register('maxAccounts', { valueAsNumber: true, min: 1 })} />
                            <SimpleTooltip content="Maximum number of programmable DSS keys"><Label htmlFor="maxDssKeys" className="text-left">Max DSS Keys</Label></SimpleTooltip>
                            <Input id="maxDssKeys" type="number" {...register('maxDssKeys', { valueAsNumber: true, min: 0 })} />

                            {/* Row 3 */}
                            <SimpleTooltip content="Maximum number of expansion modules/sidecars supported"><Label htmlFor="maxExpansionUnits" className="text-left">Max Exp. Modules</Label></SimpleTooltip>
                            <Input id="maxExpansionUnits" type="number" {...register('maxExpansionUnits', { valueAsNumber: true, min: 0 })} />
                            <SimpleTooltip content="Number of DSS keys per expansion module"><Label htmlFor="expModuleKeys" className="text-left">Exp. Module Keys</Label></SimpleTooltip>
                            <Input id="expModuleKeys" type="number" {...register('expModuleKeys', { valueAsNumber: true, min: 0 })} />

                            {/* Row 4: Wallpaper + Summer/Daylight Time */}
                            <SimpleTooltip content="Enable if this model supports custom wallpaper images"><Label className="text-left">Wallpaper</Label></SimpleTooltip>
                            <Controller control={control} name="wallpaper" render={({ field }) => (
                                <Toggle value={field.value} onChange={field.onChange} trueLabel="Yes" falseLabel="No" hideText={false} />
                            )} />
                            {showSummerTime ? (
                                <>
                                    <SimpleTooltip content="Summer time / daylight saving time mode options"><Label className="text-left">Summer Time</Label></SimpleTooltip>
                                    <Controller control={control} name="summerTimeOptions" render={({ field }) => {
                                        const modalItems = (() => { try { const p = JSON.parse(field.value || '[]'); if (Array.isArray(p)) return p.map((x: Record<string, unknown>) => ({ id: String(x['value'] ?? x['key'] ?? ''), name: String(x['label'] ?? x['name'] ?? x['value'] ?? ''), selected: x['selected'] !== false })); if (typeof p === 'object') return Object.entries(p).map(([k, v]) => ({ id: String(v), name: k, selected: true })); return []; } catch { return []; } })();
                                        const selectedIds = modalItems.filter(i => i.selected).map(i => i.id);
                                        return (<><div onClick={() => setShowSummerTimeModal(true)} className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-accent hover:text-accent-foreground"><span className={selectedIds.length === 0 ? 'text-muted-foreground' : ''}>{selectedIds.length > 0 ? `${selectedIds.length} options supported` : 'No options configured'}</span><Clock className="h-4 w-4 opacity-50" /></div><MultiSelectModal isOpen={showSummerTimeModal} onClose={() => setShowSummerTimeModal(false)} title="Summer Time Options" description="Select the summer time modes supported by this model." items={modalItems} selectedIds={selectedIds} onSelectionChange={(ids) => { field.onChange(JSON.stringify(modalItems.map(i => ({ value: i.id, label: i.name, selected: ids.includes(i.id) })))); }} /></>);
                                    }} />
                                </>
                            ) : showDaylightSavings ? (
                                <>
                                    <SimpleTooltip content="Daylight savings time configuration options"><Label className="text-left">Daylight Savings</Label></SimpleTooltip>
                                    <Controller control={control} name="daylightSavingsOptions" render={({ field }) => {
                                        const modalItems = (() => { try { const p = JSON.parse(field.value || '[]'); if (Array.isArray(p)) return p.map((x: Record<string, unknown>) => ({ id: String(x['value'] ?? x['key'] ?? ''), name: String(x['label'] ?? x['name'] ?? x['value'] ?? ''), selected: x['selected'] !== false })); if (typeof p === 'object') return Object.entries(p).map(([k, v]) => ({ id: String(v), name: k, selected: true })); return []; } catch { return []; } })();
                                        const selectedIds = modalItems.filter(i => i.selected).map(i => i.id);
                                        return (<><div onClick={() => setShowDaylightModal(true)} className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-accent hover:text-accent-foreground"><span className={selectedIds.length === 0 ? 'text-muted-foreground' : ''}>{selectedIds.length > 0 ? `${selectedIds.length} options supported` : 'No options configured'}</span><Sun className="h-4 w-4 opacity-50" /></div><MultiSelectModal isOpen={showDaylightModal} onClose={() => setShowDaylightModal(false)} title="Daylight Savings Options" description="Select the daylight savings options supported by this model." items={modalItems} selectedIds={selectedIds} onSelectionChange={(ids) => { field.onChange(JSON.stringify(modalItems.map(i => ({ value: i.id, label: i.name, selected: ids.includes(i.id) })))); }} /></>);
                                    }} />
                                </>
                            ) : (<><div /><div /></>)}

                            {/* Both Summer + Daylight  */}
                            {showSummerTime && showDaylightSavings && (
                                <>
                                    <SimpleTooltip content="Daylight savings time configuration options"><Label className="text-left">Daylight Savings</Label></SimpleTooltip>
                                    <Controller control={control} name="daylightSavingsOptions" render={({ field }) => {
                                        const modalItems = (() => { try { const p = JSON.parse(field.value || '[]'); if (Array.isArray(p)) return p.map((x: Record<string, unknown>) => ({ id: String(x['value'] ?? x['key'] ?? ''), name: String(x['label'] ?? x['name'] ?? x['value'] ?? ''), selected: x['selected'] !== false })); if (typeof p === 'object') return Object.entries(p).map(([k, v]) => ({ id: String(v), name: k, selected: true })); return []; } catch { return []; } })();
                                        const selectedIds = modalItems.filter(i => i.selected).map(i => i.id);
                                        return (<><div onClick={() => setShowDaylightModal(true)} className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-accent hover:text-accent-foreground"><span className={selectedIds.length === 0 ? 'text-muted-foreground' : ''}>{selectedIds.length > 0 ? `${selectedIds.length} options supported` : 'No options configured'}</span><Sun className="h-4 w-4 opacity-50" /></div><MultiSelectModal isOpen={showDaylightModal} onClose={() => setShowDaylightModal(false)} title="Daylight Savings Options" description="Select the daylight savings options supported by this model." items={modalItems} selectedIds={selectedIds} onSelectionChange={(ids) => { field.onChange(JSON.stringify(modalItems.map(i => ({ value: i.id, label: i.name, selected: ids.includes(i.id) })))); }} /></>);
                                    }} />
                                    <div /><div />
                                </>
                            )}

                            {/* Template file */}
                            <SimpleTooltip content="Upload the provisioning template. Supported formats: .cfg, .xml, .txt, .j2"><Label className="text-left">Template</Label></SimpleTooltip>
                            <div className="flex items-center gap-2 overflow-hidden">
                                {existingFiles.template && !templateFile && (
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs px-2 py-1 rounded-full border border-emerald-500/20 truncate max-w-[120px]">
                                            <FileCode className="h-3 w-3 shrink-0" /><span className="truncate">{existingFiles.template.split(/[/\\]/).pop()}</span>
                                        </span>
                                        <button type="button" onClick={handlePreviewFile} className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0" title="View content"><Eye className="h-3.5 w-3.5" /></button>
                                    </div>
                                )}
                                {templateFile && (
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="inline-flex items-center gap-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs px-2 py-1 rounded-full border border-blue-500/20 truncate max-w-[120px]">
                                            <FileCode className="h-3 w-3 shrink-0" /><span className="truncate">{templateFile.name}</span>
                                        </span>
                                        <button type="button" onClick={handlePreviewFile} className="p-1 text-muted-foreground hover:text-foreground transition-colors shrink-0" title="View content"><Eye className="h-3.5 w-3.5" /></button>
                                    </div>
                                )}
                                <label className="relative cursor-pointer shrink-0">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium rounded-md border border-primary/30 transition-colors ${existingFiles.template || templateFile ? '' : 'w-full justify-center'}`}>
                                        <Upload className="h-3 w-3" />{existingFiles.template || templateFile ? '' : 'Upload'}
                                    </span>
                                    <input type="file" accept=".cfg,.xml,.txt,.j2" onChange={e => handleFileChange(e, setTemplateFile)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                </label>
                            </div>

                            {/* Firmware file */}
                            <SimpleTooltip content="Upload the device firmware file (.bin, .rom, .img, .zip, etc)"><Label className="text-left">Firmware</Label></SimpleTooltip>
                            <div className="flex items-center gap-2 overflow-hidden">
                                {existingFiles.firmware && !firmwareFile && (
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="inline-flex items-center gap-1.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs px-2 py-1 rounded-full border border-purple-500/20 truncate max-w-[120px]">
                                            <Download className="h-3 w-3 shrink-0" /><span className="truncate">{existingFiles.firmware.split(/[/\\]/).pop()}</span>
                                        </span>
                                    </div>
                                )}
                                {firmwareFile && (
                                    <div className="flex items-center gap-1 min-w-0">
                                        <span className="inline-flex items-center gap-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs px-2 py-1 rounded-full border border-blue-500/20 truncate max-w-[120px]">
                                            <Download className="h-3 w-3 shrink-0" /><span className="truncate">{firmwareFile.name}</span>
                                        </span>
                                    </div>
                                )}
                                <label className="relative cursor-pointer shrink-0">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium rounded-md border border-primary/30 transition-colors ${existingFiles.firmware || firmwareFile ? '' : 'w-full justify-center'}`}>
                                        <Upload className="h-3 w-3" />{existingFiles.firmware || firmwareFile ? '' : 'Upload'}
                                    </span>
                                    <input type="file" accept=".bin,.rom,.img,.zip,.tar,.gz,.hex" onChange={e => handleFileChange(e, setFirmwareFile)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                </label>
                            </div>
                        </div>
                    </FormBox>

                    <DeviceModelAdvancedSection
                        control={control}
                        availableDssOptions={availableDssOptions}
                        showDssModal={showDssModal} setShowDssModal={setShowDssModal}
                        showTimezoneModal={showTimezoneModal} setShowTimezoneModal={setShowTimezoneModal}
                        showTimeFormatModal={showTimeFormatModal} setShowTimeFormatModal={setShowTimeFormatModal}
                        showDateFormatModal={showDateFormatModal} setShowDateFormatModal={setShowDateFormatModal}
                        showLanguageModal={showLanguageModal} setShowLanguageModal={setShowLanguageModal}
                        showSummerTimeModal={showSummerTimeModal} setShowSummerTimeModal={setShowSummerTimeModal}
                        showDaylightModal={showDaylightModal} setShowDaylightModal={setShowDaylightModal}
                        showSummerTime={showSummerTime}
                        showDaylightSavings={showDaylightSavings}
                    />
                </form>

                <FixedActionBar
                    onSaveWithAction={handleSaveAction}
                    onCancel={() => reset()}
                    onDelete={isEdit && !isSystemModel ? () => setDeleteModal({ isOpen: true, modelId: id! }) : undefined}
                    isEditMode={isEdit}
                    isSaving={isSaving}
                    cancelText={t('common.cancel', 'Cancel')}
                    deleteText={t('common.delete', 'Delete')}
                    useSaveDropdown={true}
                    saveDropdownStorageKey="device_models:saveAction"
                />

                <ConfirmationModal
                    isOpen={deleteModal.isOpen}
                    onClose={() => setDeleteModal({ isOpen: false, modelId: null })}
                    onConfirm={handleDelete}
                    title={t('common.deleteItem', 'Delete Model')}
                    message={t('common.confirmDelete', 'Are you sure you want to delete this device model?')}
                    confirmText={t('common.delete', 'Delete')}
                    cancelText={t('common.cancel', 'Cancel')}
                    type="danger"
                />

                <QuickSearchModal
                    isOpen={showQuickList}
                    onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
                    title="Quick Search Models"
                    items={quickListItems}
                    isLoadingInitial={false}
                    isSearching={false}
                    searchValue={quickListSearch}
                    onSearchChange={setQuickListSearch}
                    onItemSelect={(model: { id: string | number; modelName?: string; vendor?: { name: string } }) => {
                        setShowQuickList(false);
                        navigate(`/settings/provisioning/provisioning-device-models/${model.id}`);
                    }}
                    currentItemId={id}
                    searchPlaceholder="Search by model or vendor..."
                    emptyMessage="Start typing to search models..."
                    noResultsMessage="No models found"
                    headerIcon={<Smartphone className="h-5 w-5" />}
                    minSearchChars={1}
                    renderItem={(model: { id: string | number; modelName?: string; vendor?: { name: string }; isSystem?: boolean; imageFile?: string }, isCurrent: boolean) => (
                        <button onClick={() => { if (!isCurrent) { setShowQuickList(false); setQuickListSearch(''); navigate(`/settings/provisioning/provisioning-device-models/${model.id}`); } }}
                            disabled={isCurrent}
                            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}>
                            <div className="flex items-center gap-3">
                                {model.imageFile ? (
                                    <img src={`/uploads/${model.imageFile.replace(/\\/g, '/')}`} alt={model.modelName} className="h-8 w-8 object-contain rounded bg-white p-0.5 border border-gray-100 dark:border-gray-700" />
                                ) : (
                                    <div className="h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-gray-400"><Smartphone className="h-4 w-4" /></div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-foreground">{model.modelName}</span>
                                        {isCurrent && <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">Current</span>}
                                    </div>
                                    <div className="text-sm text-muted-foreground truncate">{model.vendor?.name}</div>
                                </div>
                            </div>
                        </button>
                    )}
                />

                <DeviceModelFilePreview
                    isOpen={showFilePreview}
                    onClose={() => setShowFilePreview(false)}
                    fileName={previewFileName}
                    content={fileContent}
                    onDownload={handleDownload}
                />
            </div>
        </div>
    );
}
